/**
 * Portal staff paste normal Google Drive sharing links. The dashboard converts
 * them to the embeddable /preview form before loading them for the client.
 */

const FILE_ID_PATTERNS: RegExp[] = [
  /\/file\/d\/([a-zA-Z0-9_-]{10,})/, // .../file/d/FILE_ID/view
  /\/d\/([a-zA-Z0-9_-]{10,})/, // docs.google.com/.../d/FILE_ID/edit
  /[?&]id=([a-zA-Z0-9_-]{10,})/, // open?id=FILE_ID, uc?export=download&id=FILE_ID
]

export function extractDriveFileId(url: string): string {
  const value = (url ?? '').trim()
  if (!value) return ''
  if (isDriveFolder(value)) return ''
  for (const pattern of FILE_ID_PATTERNS) {
    const match = value.match(pattern)
    if (match) return match[1]
  }
  return ''
}

export function isDriveUrl(url: string): boolean {
  return /(?:drive|docs)\.google\.com/.test((url ?? '').trim())
}

export function isDriveFolder(url: string): boolean {
  const value = (url ?? '').trim()
  if (/\/(?:drive\/)?(?:u\/\d+\/)?folders\//.test(value)) return true
  /* drive.google.com/open?id=... is the legacy share form Drive still
     produces for folders — a modern file share always uses /file/d/<id>/view
     instead, so a bare open?id= on the drive.google.com host (not
     docs.google.com, which uses this form for Docs/Sheets/Slides files) is
     treated as a folder link rather than sent through file-id extraction,
     where it would otherwise "succeed" and embed Google's own broken-preview
     page inside our iframe instead of failing cleanly. */
  return /^https?:\/\/drive\.google\.com\/open(?:\?|$)/.test(value)
}

/** Forces the Drive player UI into English rather than the viewer's region. */
function withEnglish(url: string): string {
  if (/[?&]hl=/.test(url)) return url
  return `${url}${url.includes('?') ? '&' : '?'}hl=en`
}

/** Returns an embeddable URL, or '' when the link cannot be embedded. */
export function toDrivePreviewUrl(url: string): string {
  const value = (url ?? '').trim()
  if (!value) return ''

  const fileId = extractDriveFileId(value)
  if (fileId) return `https://drive.google.com/file/d/${fileId}/preview?hl=en`

  // Already an embeddable Drive preview, or a non-Drive embed (e.g. Vimeo/YouTube embed).
  if (/\/preview(\?|$)/.test(value)) return isDriveUrl(value) ? withEnglish(value) : value
  if (!isDriveUrl(value) && /^https?:\/\//.test(value) && /embed|player|\.mp4($|\?)/.test(value)) {
    return value
  }

  return ''
}

/**
 * A real poster frame for a Drive file, pulled from Drive's own public
 * thumbnail host — the same one Drive uses for its own file icons and grid
 * previews. It needs no access token, which works here for exactly the
 * reason the /preview iframe above already works without one: every
 * deliverable a client can open is already shared such that anyone with the
 * link can view it. Folders resolve to '' (extractDriveFileId already
 * refuses those), and a file Drive can't generate a thumbnail for — or one
 * whose sharing is actually locked down tighter than the rest — simply
 * fails to load as an <img>; callers fall back to the plain type icon
 * rather than trying to detect that in advance. `size` is the requested
 * pixel width; Drive returns the image scaled to fit, not cropped to it.
 */
export function toDriveThumbnailUrl(url: string, size = 200): string {
  const fileId = extractDriveFileId(url)
  if (!fileId) return ''
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${size}`
}

/**
 * One photograph out of a gallery folder, by its Drive FILE ID rather than a
 * link — the ids come from the `client-photos` Edge Function, which is the
 * only thing allowed to turn a deliverable into a folder listing.
 *
 * Same host and same reasoning as toDriveThumbnailUrl above: no credential,
 * because the folder is shared "anyone with the link" and its contents
 * inherit that. Nothing is proxied through Supabase — a 100-photograph shoot
 * would be gigabytes of someone else's bandwidth to hide a sharing mistake
 * staff should fix instead. A folder that is NOT link-shared simply fails to
 * load as an <img>, and the gallery falls back to the Drive link.
 *
 * `width` is what to ask Drive for, not what to draw: request roughly twice
 * the CSS width so the grid stays sharp on a retina screen, and something
 * large for the lightbox. Drive scales to fit, it does not crop.
 */
export function driveImageUrl(fileId: string, width: number): string {
  if (!fileId) return ''
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w${Math.round(width)}`
}

/** The original file, at full resolution, as a download rather than a view. */
export function driveDownloadUrl(fileId: string): string {
  if (!fileId) return ''
  return `https://drive.google.com/uc?export=download&id=${fileId}`
}

export const EMBED_ERROR =
  'Preview unavailable — this may be a folder link rather than a file, or the sharing permissions block preview. Share the individual file’s link, or open it directly in Google Drive.'
